import { useState } from 'react';
import { Box, Container, IconButton, Menu, MenuItem, Theme, useMediaQuery, Button } from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';

import { LogOutIcon, MenuIcon } from 'assets';
import { IBurgerMenuContext, IFirstLoginTooltipsContext } from 'context';
import { useBurgerMenu, useFirstLoginTooltips } from 'hooks';
import { SubscriptionStatusBanner } from 'components/SubscriptionStatusBanner';
import { useAppSelector } from 'store';
import { selectUser } from 'store/user';
import { ProfileSection } from './components/ProfileSection';
import { FirstLoginTooltip } from 'components';
import { ApiUserTooltipStagePassedEnum } from 'types';
import { isHipaaLink } from '../../../config/productProfile';
import { SECOND_ACCOUNT_PAYMENT_REQUIRED } from 'constants/index';
import { getIsAdminByRoles } from 'utils';
import { LanguageSelector } from 'components/LanguageSelector/LanguageSelector';
import { IUserContext } from 'context';
import {
  getHipaaLogoSrc,
  getVia3LogoSrc,
  createThemeOptions,
  getVisibleThemePreference,
  hasStoredThemePreference,
  renderThemeIcon,
  visibleThemePreferences,
} from 'theme';
import { useThemePreference, useUser } from 'hooks';

import styles from './Header.module.scss';

export function Header() {
  const { t: tAuth, i18n } = useTranslation('auth');
  const { t: tProviderRoom } = useTranslation('providerRoom');
  const navigate = useNavigate();
  const user = useAppSelector(selectUser);
  const theme = useTheme();
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));
  const isTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const { showMenu }: IBurgerMenuContext = useBurgerMenu();

  const { tooltipsState, tooltipStagePassed, accept }: IFirstLoginTooltipsContext = useFirstLoginTooltips();
  const accountSettingsText = tAuth('headerAccountSettingsTooltip');

  const finalStepText = isHipaaLink ? tAuth('headerFinalStepHipaa') : tAuth('headerFinalStepVia3');

  const location = useLocation();
  const { logout }: IUserContext = useUser();

  const isVia3HomePage = !isHipaaLink && location.pathname === '/' && !user;
  const isLoginPage = location.pathname === '/login';
  const isRegisterFlowPage = location.pathname.startsWith('/register');
  const isRegisterSecondAccountPage = location.pathname === '/registerSecondAccount';
  const isAuthEntryPage = isLoginPage || isRegisterFlowPage;
  const isPatientWaitingRoom = !isHipaaLink && location.pathname !== '/' && !isLoginPage && !user;
  const isPublicWaitingRoom = location.pathname !== '/' && !isLoginPage && !isRegisterFlowPage && !user;
  const isRtl = i18n.dir() === 'rtl';
  const { themePreference, setThemePreference } = useThemePreference();
  const resolvedThemeMode = theme.customMode || theme.palette.mode;
  const themePreferenceForUi = getVisibleThemePreference(themePreference);
  const shouldForceLightPublicVia3Logo = (isVia3HomePage || isPatientWaitingRoom) && !hasStoredThemePreference();
  const hipaaLogoSrc = getHipaaLogoSrc(resolvedThemeMode);
  const via3LogoSrc = getVia3LogoSrc(resolvedThemeMode);
  const logoSrc = isHipaaLink ? hipaaLogoSrc : shouldForceLightPublicVia3Logo ? getVia3LogoSrc('light') : via3LogoSrc;
  const isUnpaidSecondAccountCandidate =
    !!user &&
    getIsAdminByRoles(user.clinicUser.roles || []) &&
    user.clinicUser.createRoomSkipped &&
    !user.clinicUser.subscriptionInfo &&
    user.clinicUser.via3UserType !== 'Member';
  const logoHref =
    user && (localStorage.getItem(SECOND_ACCOUNT_PAYMENT_REQUIRED) === 'true' || isUnpaidSecondAccountCandidate)
      ? '/registerSecondAccount'
      : '/';
  const [themeMenuAnchorEl, setThemeMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [mobileTooltipAnchorEl, setMobileTooltipAnchorEl] = useState<HTMLButtonElement | null>(null);
  const isThemeMenuOpen = Boolean(themeMenuAnchorEl);
  const loginLanguageWidth = isMobile ? 128 : 128;
  const themeOptions = createThemeOptions({
    light: tProviderRoom('light'),
    dark: tProviderRoom('dark'),
    system: tProviderRoom('system'),
    calm: tProviderRoom('calm'),
    nature: tProviderRoom('nature'),
  }).filter((option) => visibleThemePreferences.includes(option.mode));

  const handleThemeSelect = (mode: typeof themePreference) => {
    setThemePreference(mode);
    setThemeMenuAnchorEl(null);
  };

  const handleSignOut = (): void => {
    navigate('/');
    logout();
  };

  return (
    <Box component='div' className={styles.outerWrapper}>
      {!isMobile && <SubscriptionStatusBanner />}
      <Box component='header' className={styles.headerWrapper}>
        <Container>
          <FirstLoginTooltip
            text={tooltipsState === 2 ? accountSettingsText : finalStepText}
            numberText={tooltipsState === 2 ? tAuth('headerStepTwoOfThree') : tAuth('headerStepThreeOfThree')}
            open={
              (tooltipsState === 2 || tooltipsState === 3) &&
              tooltipStagePassed === ApiUserTooltipStagePassedEnum.None &&
              isDesktop
            }
            onClick={accept}
            actionText={tooltipsState === 2 ? tAuth('next') : tAuth('done')}
            arrowClassName={styles.tooltipArrow}
          >
            <Box
              className={classnames(styles.headerPrimary, {
                [styles.headerPrimaryTablet]: isTablet,
                [styles.headerPrimaryMobile]: isMobile,
                [styles.headerVia3HomeMobile]: isMobile && isVia3HomePage,
                [styles.headerPatientWaitingRoomMobile]: isMobile && isPublicWaitingRoom,
                [styles.headerPublicWaitingRoomRtl]: (isPublicWaitingRoom || isAuthEntryPage) && isRtl,
                [styles.headerLoginPage]: isAuthEntryPage,
              })}
            >
              {user && !isDesktop && !isAuthEntryPage && (
                <FirstLoginTooltip
                  text={tooltipsState === 2 ? accountSettingsText : finalStepText}
                  numberText={tooltipsState === 2 ? tAuth('headerStepTwoOfThree') : tAuth('headerStepThreeOfThree')}
                  open={
                    (tooltipsState === 2 || tooltipsState === 3) &&
                    tooltipStagePassed === ApiUserTooltipStagePassedEnum.None
                  }
                  onClick={accept}
                  actionText={tooltipsState === 2 ? tAuth('next') : tAuth('done')}
                  placement='bottom-start'
                  anchorEl={mobileTooltipAnchorEl}
                  popperOffset={[0, 10]}
                  className={styles.mobileTooltip}
                  arrowClassName={styles.mobileTooltipArrow}
                >
                  <IconButton onClick={showMenu} ref={setMobileTooltipAnchorEl}>
                    <MenuIcon color='primary' className={styles.icon} />
                  </IconButton>
                </FirstLoginTooltip>
              )}

              <Box className={styles.brandWithLanguage}>
                <a href={logoHref} className={styles.logoLink}>
                  <img
                    src={logoSrc}
                    alt={isHipaaLink ? 'HIPAA LINK' : 'VIA3'}
                    className={isHipaaLink ? styles.logo : styles.logoVia3}
                  />
                </a>
              </Box>

              {isPublicWaitingRoom && (
                <Box className={styles.patientWaitingRoomControls}>
                  <Button
                    variant='outlined'
                    className={styles.themeSelectButton}
                    onClick={(e) => setThemeMenuAnchorEl(e.currentTarget)}
                    startIcon={renderThemeIcon(themePreferenceForUi, styles.themeToggleIcon)}
                    endIcon={<KeyboardArrowDown className={styles.themeArrowIcon} />}
                    sx={
                      isThemeMenuOpen
                        ? {
                            borderBottomLeftRadius: '0 !important',
                            borderBottomRightRadius: '0 !important',
                            borderBottomColor: 'transparent !important',
                          }
                        : undefined
                    }
                  />
                  <Menu
                    anchorEl={themeMenuAnchorEl}
                    open={isThemeMenuOpen}
                    onClose={() => setThemeMenuAnchorEl(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    MenuListProps={{ disablePadding: true }}
                    PaperProps={{
                      sx: {
                        minWidth: 116,
                        width: 'max-content',
                        marginTop: '-1px',
                        borderTop: 'none',
                        borderTopLeftRadius: 0,
                        borderTopRightRadius: 0,
                        '& .MuiMenuItem-root': {
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                        },
                        '& .MuiMenuItem-root:hover': {
                          backgroundColor: 'rgba(25, 118, 210, 0.08)',
                        },
                        '& .MuiMenuItem-root.Mui-selected': {
                          backgroundColor: 'rgba(25, 118, 210, 0.14)',
                        },
                        '& .MuiMenuItem-root.Mui-selected:hover': {
                          backgroundColor: 'rgba(25, 118, 210, 0.2)',
                        },
                      },
                    }}
                  >
                    {themeOptions.map((option) => {
                      const isSelected = themePreferenceForUi === option.mode;
                      return (
                        <MenuItem
                          key={option.mode}
                          onClick={() => handleThemeSelect(option.mode)}
                          selected={isSelected}
                        >
                          {renderThemeIcon(option.mode, styles.themeMenuItemIcon)}
                          {option.label}
                        </MenuItem>
                      );
                    })}
                  </Menu>
                  <LanguageSelector
                    width={52}
                    compact
                    compactMenuWidth={isMobile ? 115 : 128}
                    menuAlign={isRtl ? 'center' : 'right'}
                    denseMenuItems
                    sx={{ height: '36px' }}
                  />
                </Box>
              )}

              {isAuthEntryPage && (
                <Box className={styles.loginLanguage}>
                  <LanguageSelector
                    width={loginLanguageWidth}
                    compact={isMobile}
                    compactShowLabel={isMobile}
                    compactMenuWidth={loginLanguageWidth}
                  />
                  {isRegisterSecondAccountPage && (
                    <Button
                      variant='outlined'
                      className={styles.logoutButton}
                      onClick={handleSignOut}
                      startIcon={<LogOutIcon color='primary' className={styles.logoutButtonIcon} />}
                    >
                      {tAuth('profileSectionMenu.logOut')}
                    </Button>
                  )}
                </Box>
              )}

              {isVia3HomePage && (
                <Box className={styles.via3Actions}>
                  <Button
                    variant='outlined'
                    className={styles.themeSelectButton}
                    onClick={(e) => setThemeMenuAnchorEl(e.currentTarget)}
                    startIcon={renderThemeIcon(themePreferenceForUi, styles.themeToggleIcon)}
                    endIcon={<KeyboardArrowDown className={styles.themeArrowIcon} />}
                    sx={
                      isThemeMenuOpen
                        ? {
                            borderBottomLeftRadius: '0 !important',
                            borderBottomRightRadius: '0 !important',
                            borderBottomColor: 'transparent !important',
                          }
                        : undefined
                    }
                  />
                  <Menu
                    anchorEl={themeMenuAnchorEl}
                    open={isThemeMenuOpen}
                    onClose={() => setThemeMenuAnchorEl(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    MenuListProps={{ disablePadding: true }}
                    PaperProps={{
                      sx: {
                        minWidth: 116,
                        width: 'max-content',
                        marginTop: '-1px',
                        borderTop: 'none',
                        borderTopLeftRadius: 0,
                        borderTopRightRadius: 0,
                        '& .MuiMenuItem-root': {
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                        },
                        '& .MuiMenuItem-root:hover': {
                          backgroundColor: 'rgba(25, 118, 210, 0.08)',
                        },
                        '& .MuiMenuItem-root.Mui-selected': {
                          backgroundColor: 'rgba(25, 118, 210, 0.14)',
                        },
                        '& .MuiMenuItem-root.Mui-selected:hover': {
                          backgroundColor: 'rgba(25, 118, 210, 0.2)',
                        },
                      },
                    }}
                  >
                    {themeOptions.map((option) => {
                      const isSelected = themePreferenceForUi === option.mode;
                      return (
                        <MenuItem
                          key={option.mode}
                          onClick={() => handleThemeSelect(option.mode)}
                          selected={isSelected}
                        >
                          {renderThemeIcon(option.mode, styles.themeMenuItemIcon)}
                          {option.label}
                        </MenuItem>
                      );
                    })}
                  </Menu>
                  <Button
                    variant='contained'
                    className={styles.staffLoginButton}
                    onClick={() => {
                      const url = process.env.REACT_APP_HOME_PAGE_FOR_NON_AUTH_URL;
                      if (url) {
                        window.location.href = url;
                      }
                    }}
                  >
                    {tAuth('staffLogin')}
                  </Button>
                </Box>
              )}

              {/* {user && !isDesktop && (
              <IconButton>
                <BellIcon color='primary' className={styles.icon} />
              </IconButton>
            )} */}

              {user && isDesktop && !isAuthEntryPage && (
                <Box className={styles.profile}>
                  <ProfileSection />
                </Box>
              )}
            </Box>
          </FirstLoginTooltip>
        </Container>
      </Box>
    </Box>
  );
}
